import React, { useState } from 'react';
import PaintingHeader from './PaintingHeader';
import PaintingFooter from './PaintingFooter';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { X, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  const projects = [
    {
      category: "Residential Interior",
      images: [
        {
          before: "https://images.unsplash.com/photo-1513694203232-719a280e022f?w=600&h=400&fit=crop",
          after: "https://images.unsplash.com/photo-1724582586529-62622e50c0b3?w=600&h=400&fit=crop",
          title: "Modern Living Room Transformation",
          location: "Hollywood, FL"
        },
        {
          before: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=600&h=400&fit=crop",
          after: "https://images.unsplash.com/photo-1628744876497-eb30460be9f6?w=600&h=400&fit=crop",
          title: "Bedroom Refresh",
          location: "Fort Lauderdale, FL"
        },
        {
          before: "https://images.unsplash.com/photo-1556228578-dd6f8cfc2cc2?w=600&h=400&fit=crop",
          after: "https://images.unsplash.com/photo-1556912167-f556f1f39faa?w=600&h=400&fit=crop",
          title: "Kitchen Cabinet Painting",
          location: "Pembroke Pines, FL"
        }
      ]
    },
    {
      category: "Residential Exterior",
      images: [
        {
          before: "https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?w=600&h=400&fit=crop",
          after: "https://images.unsplash.com/photo-1638945657536-60259aae3c34?w=600&h=400&fit=crop",
          title: "Complete Exterior Makeover",
          location: "Hollywood, FL"
        },
        {
          before: "https://images.unsplash.com/photo-1570129477492-45c003edd2be?w=600&h=400&fit=crop",
          after: "https://images.unsplash.com/photo-1555636222-cae831e670b3?w=600&h=400&fit=crop",
          title: "Modern Home Exterior",
          location: "Miramar, FL"
        }
      ]
    },
    {
      category: "Commercial Projects",
      images: [
        {
          before: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=600&h=400&fit=crop",
          after: "https://images.unsplash.com/photo-1739820460102-1efe9165997f?w=600&h=400&fit=crop",
          title: "Office Building Renovation",
          location: "Hollywood, FL"
        },
        {
          before: "https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=600&h=400&fit=crop",
          after: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=600&h=400&fit=crop",
          title: "Retail Space Update",
          location: "Fort Lauderdale, FL"
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <PaintingHeader />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#1E3A8A] to-[#1E40AF] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm font-semibold mb-4">
            📸 Our Work
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-none mb-6">
            Project Gallery
          </h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            See the transformations we've delivered for homeowners and businesses throughout South Florida. Quality speaks for itself.
          </p>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {projects.map((category) => (
            <div key={category.category} className="mb-20 last:mb-0">
              <div className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-black text-[#0F172A] mb-2">
                  {category.category}
                </h2>
                <div className="w-24 h-1 bg-[#EA580C] mx-auto"></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {category.images.map((project) => (
                  <Card 
                    key={project.title} 
                    className="group overflow-hidden hover:shadow-2xl transition-all duration-300 border-2 hover:border-[#1E3A8A] cursor-pointer"
                    onClick={() => setSelectedImage(project)}
                  >
                    <div className="relative">
                      {/* Before/After Split View */}
                      <div className="grid grid-cols-2 gap-0">
                        <div className="relative aspect-square overflow-hidden">
                          <div className="absolute top-2 left-2 z-10 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">
                            BEFORE
                          </div>
                          <img 
                            src={project.before} 
                            alt={`${project.title} - Before`}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          />
                        </div>
                        <div className="relative aspect-square overflow-hidden">
                          <div className="absolute top-2 right-2 z-10 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded">
                            AFTER
                          </div>
                          <img 
                            src={project.after} 
                            alt={`${project.title} - After`}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          />
                        </div>
                      </div>
                    </div>
                    <CardContent className="p-6">
                      <h3 className="text-xl font-bold text-[#0F172A] mb-2">
                        {project.title}
                      </h3>
                      <p className="text-[#64748B]">{project.location}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button 
            className="absolute top-4 right-4 text-white hover:text-[#EA580C] transition-colors"
            onClick={() => setSelectedImage(null)}
          >
            <X className="w-8 h-8" />
          </button>
          <div className="max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="bg-white rounded-2xl overflow-hidden">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                <div className="relative">
                  <div className="absolute top-4 left-4 z-10 bg-red-500 text-white text-sm font-bold px-3 py-2 rounded">
                    BEFORE
                  </div>
                  <img 
                    src={selectedImage.before} 
                    alt="Before"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="relative">
                  <div className="absolute top-4 right-4 z-10 bg-green-500 text-white text-sm font-bold px-3 py-2 rounded">
                    AFTER
                  </div>
                  <img 
                    src={selectedImage.after} 
                    alt="After"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="p-8 text-center">
                <h3 className="text-2xl font-bold text-[#0F172A] mb-2">
                  {selectedImage.title}
                </h3>
                <p className="text-[#64748B]">{selectedImage.location}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Section */}
      <section className="py-20 bg-[#FAFAF8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { number: '500+', label: 'Projects Completed' },
              { number: '20+', label: 'Years Experience' },
              { number: '100%', label: 'Satisfaction Rate' },
              { number: '5★', label: 'Average Rating' }
            ].map((stat) => (
              <div key={stat.label}>
                <div className="text-4xl sm:text-5xl font-black text-[#1E3A8A] mb-2">
                  {stat.number}
                </div>
                <div className="text-[#64748B] font-medium">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#1E3A8A] to-[#1E40AF] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mb-6">
            Want Results Like These?
          </h2>
          <p className="text-xl text-white/90 mb-8 leading-relaxed">
            Let's transform your property next! Get your free quote today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="w-full sm:w-auto bg-[#EA580C] hover:bg-[#C2410C] text-white text-lg px-8 py-6">
                Request Free Quote
              </Button>
            </Link>
            <a href="tel:+19548172046">
              <Button size="lg" variant="outline" className="w-full sm:w-auto bg-white text-[#1E3A8A] hover:bg-white/90 text-lg px-8 py-6">
                <Phone className="w-5 h-5 mr-2" />
                Call (954) 817-2046
              </Button>
            </a>
          </div>
        </div>
      </section>

      <PaintingFooter />
    </div>
  );
};

export default Gallery;